<template>
  <div class="row h-100">
    <div class="col-12 h-100 d-flex flex-column">
      <div class="d-flex justify-content-end align-items-center mb-3 pb-1">
        <b-button v-b-modal="`generate-report-pdf-${reportId}`" variant="outline-primary" size="lg" class="px-4 mr-3" :disabled="isGenerating">
          <b-spinner v-if="isGenerating" small />
          {{ isGenerating ? $translate('Generating') : $translate('Generate PDF') }}
        </b-button>
        <GenerateReportPdfModal :report-id="reportId" :generatePDF="generatePDF" />
        <b-button
          variant="outline-primary"
          size="lg"
          v-if="$route.params.reportId"
          class="zip-link"
          :href="`${localBasePath}/api/v1/reports/report/${$route.params.reportId}/zip?authorization=${access_token_link}`"
          target="_blank"
        >
          {{ 'Download zip' | i18n }}
        </b-button>
        <b-button v-if="isSaving" variant="primary" size="lg" class="pl-4 pr-4 ml-3" disabled>
          <b-spinner small />
          {{ 'saving' | i18n }}
        </b-button>
        <b-button
          v-else-if="reportData.status !== REPORT_STATUSES.COMPLETED"
          variant="primary"
          size="lg"
          class="pl-4 pr-4 ml-3"
          :disabled="isSaving || isSubmitting"
          @click="saveReport"
        >
          {{ 'save' | i18n }}
        </b-button>
        <b-button v-if="isSubmitting" variant="primary" size="lg" class="pl-4 pr-4 ml-3" disabled>
          <b-spinner small />
          {{ 'saving' | i18n }}
        </b-button>
        <b-button
          v-else-if="reportData.status !== REPORT_STATUSES.COMPLETED"
          v-b-modal.submit-report-details
          variant="primary"
          size="lg"
          class="pl-4 pr-4 ml-3"
          :disabled="isSaving || isSubmitting"
        >
          {{ 'submit_for_report' | i18n }}
        </b-button>
        <SubmitReportDetails :on-submit-report="submitReport" />
      </div>
      <div v-if="selectedTemplate">
        <div v-if="reportData && reportData.user && Object.keys(reportData).length !== 0">
          <h3 class="m-0 py-1 px-0">{{ reportData.title }} / Report #: {{ reportData.caseNumber }}</h3>
          <div class="user-block mb-2">Person: {{ reportData.user.name }} ({{ reportData.user.email }})</div>
        </div>
      </div>
      <div v-if="selectedTemplate" class="overflow-auto overflow-x-hidden pr-1">
        <Group
          v-for="group in selectedTemplate.groups"
          :key="group.id"
          :group="group"
          :set-field-value="setFieldValue"
          :fields-value="fieldsValue"
          :lists="lists"
          :getFileURL="getFileURL"
          :getFile="getFile"
          :excludeFromPDFValues="excludeFromPDFValues"
          :setExcludeFromPDFValue="setExcludeFromPDFValue"
          groupName="Group"
          :showValidation="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

import { VALIDATION_RULES, REPORT_STATUSES, ENTRY_TYPES_FILES, CASE_STATUSES } from '../../../../../../../../constants';
import { ReportsMixin, AuthStorageMixin, NotificationMixin, ListMixin } from '../../../../../mixins';
import Group from '../components/group';
import SubmitReportDetails from './submit-modal';
import GenerateReportPdfModal from './modal/pdf-generate-modal';
import Notification from '../../../common/notification';

import api from '../../../../api';

export default {
  name: 'CaseReportDetails',
  components: { Group, SubmitReportDetails, GenerateReportPdfModal },
  mixins: [ReportsMixin, AuthStorageMixin, NotificationMixin, ListMixin],
  data() {
    return {
      REPORT_STATUSES: REPORT_STATUSES,
      ENTRY_TYPES_FILES: ENTRY_TYPES_FILES,
      lists: [],
      fieldsValue: {},
      access_token_link: '',
      reportData: {},
      isSaving: false,
      isSubmitting: false,
      excludeFromPDFValues: {},
      isGenerating: false,
      id: '',
      reportId: '',
    };
  },
  computed: {
    selectedTemplate: function () {
      if (!this.reportData.template || !this.reportData.id) {
        return null;
      }
      const template = this.reportData.template;
      const groups = [];
      const groupsFields = {};

      template.fields.forEach((f) => {
        if (!groupsFields[f.groupId]) {
          groupsFields[f.groupId] = [];
        }
        f.required = f.rules.some((r) => r.name === VALIDATION_RULES.REQUIRED);
        if (f.editByMobile) {
          groupsFields[f.groupId].push(f);
        }
      });

      template.groups.forEach((g) => {
        const data = {
          name: g.name,
          id: g.id,
          fields: groupsFields[g.id],
        };
        groups.push(data);

        this.$set(this.excludeFromPDFValues, g.id, this.reportData.excludeFromPDF[g.id] || false);
      });

      return {
        name: template.name,
        id: template.id,
        groups,
      };
    },
    localBasePath: function () {
      return axios.defaults.baseURL;
    },
  },
  async created() {
    this.id = this.$route.params.id;
    this.reportId = this.$route.params.reportId;
    const auth = this.authStorageService.getAuth();
    this.access_token_link = auth.access_token_link;

    if (this.$route.params.reportId) {
      const report = await this.getReport();
      const reportData = report.data;
      this.reportData = reportData;
      this.fieldsValue = {
        ...reportData.report.fields,
        ...reportData.fields,
      };
    } else {
      const { data: reportData } = await this.reportsService.createReport(this.$route.params.id, false);
      this.reportData = reportData;
      this.fieldsValue = {
        ...reportData.report.fields,
        ...reportData.fields,
      };
      this.$router.push({
        name: 'case-report-details',
        params: { id: this.$route.params.id, reportId: reportData.id },
      });
    }

    const lists = await this.listService.get();
    this.lists = lists.data;
  },
  methods: {
    setExcludeFromPDFValue(key, value) {
      this.excludeFromPDFValues[key] = value;
    },
    getFileURL(id) {
      const token = this.access_token_link;
      const reportId = this.$route.params.reportId;
      return `${this.localBasePath}/api/v1/reports/report/${reportId}/files/${id}?authorization=${token}`;
    },
    async getFile(id) {
      try {
        await axios(this.getFileURL(id));
        window.open(this.getFileURL(id), '_blank');
      } catch (error) {
        this.showNotification({ type: 'error', message: this.$translate('file_do_not_uploaded') });
      }
    },
    async setFieldValue(field, value) {
      const isFile = ENTRY_TYPES_FILES.includes(field.entryType);
      if (isFile) {
        this.fieldsValue[field.id] = value;
        if (!value) {
          this.fieldsValue[field.key] = value;
        }
      } else {
        this.fieldsValue[field.key] = value;
      }
    },
    async getReport() {
      const response = await this.reportsService.getReportById(this.$route.params.reportId);
      if (!response.data.user) {
        response.data.user = {};
      }
      this.report = response.data;
      return response;
    },
    generateNewReportData() {
      const templateFields = {};
      this.reportData.template.fields.forEach((currentField) => {
        templateFields[currentField.key] = currentField;
      });
      const fields = Object.keys(templateFields).map((key) => ({
        key,
        rules: templateFields[key].rules,
        value: this.fieldsValue[templateFields[key].id] || this.fieldsValue[key],
        entryType: templateFields[key].entryType,
        isAttachFileToEmail: Object.hasOwn(this.fieldsValue || {}, `${key}_attachToEmail`)
          ? this.fieldsValue[`${key}_attachToEmail`]
          : templateFields[key].isAttachFileToEmail,
      }));
      const reportData = { fields, textInputValue: {}, excludeFromPDFValues: this.excludeFromPDFValues };
      return reportData;
    },
    async onChangeReport({ isComplete = false, isChangeCaseStatus }) {
      try {
        if (isComplete) {
          this.isSubmitting = true;
        } else {
          this.isSaving = true;
        }

        const reportData = this.generateNewReportData();
        if (isComplete) {
          reportData.forceValidation = true;
          await this.reportsService.completeReportById(this.$route.params.reportId, reportData);
        } else {
          await this.reportsService.updateReportById(this.$route.params.reportId, reportData);
        }

        if (isChangeCaseStatus) {
          await this.reportsService.changeCaseStatus({ id: this.$route.params.id, status: CASE_STATUSES.FINISHED });
        }

        this.showNotification({ type: 'success', message: this.$translate('success') });
        this.$router.push({
          name: 'case-details',
          params: { id: this.$route.params.id },
        });
      } catch (error) {
        this.showNotification({ type: 'error', message: error.message });
      } finally {
        if (isComplete) {
          this.isSubmitting = false;
        } else {
          this.isSaving = false;
        }
      }
    },
    async saveReport() {
      await this.onChangeReport({ isComplete: false });
    },
    async submitReport(isChangeCaseStatus) {
      await this.onChangeReport({ isComplete: true, isChangeCaseStatus });
    },
    async generatePDF(language) {
      console.log('clicked');
      try {
        this.isGenerating = true;
        await api.report.generatePDF({ id: this.reportId, language });
        this.intervalIndex = setInterval(async () => {
          const { data } = await api.report.getReportById(this.reportId);
          const { reportPdf } = data;
          if (reportPdf) {
            const url = `${axios.defaults.baseURL}/api/v1/reports/report/${this.reportId}/reportPDF?authorization=${this.access_token_link}`;
            fetch(url)
              .then((resp) => resp.blob())
              .then((blob) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.style.display = 'none';
                a.href = url;
                a.download = reportPdf;
                document.body.appendChild(a);
                a.click();
                window.URL.revokeObjectURL(url);
              })
              .catch((err) => {
                this.showNotification({ type: 'error', message: err.message });
              });
            clearInterval(this.intervalIndex);
            this.isGenerating = false;
            Notification({ type: 'success', message: this.$translate('file_has_been_downloaded_successful') });
          }
        }, 3000);
      } catch (error) {
        Notification({ type: 'error', message: error.message });
        this.isGenerating = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
::v-deep .user-block {
  font-size: 13px;
  font-weight: 400;
}
.overflow-x-hidden {
  overflow-x: hidden !important;
}
</style>
